import React, { useCallback, useEffect, useRef, useState } from 'react';
import Pager from './components/Pager';
import SearchForm from './components/SearchForm';
import StoreEventRow from './components/StoreEventRow';
import { SearchResult } from './models/SearchResult';

function App(props: { id: number }) {

  const [result, errorMessage, isLoading, operators] = useEventSearch(
    props.id,
    { area: '', dateEnd: '', dateStart: '', page: 1, storeName: '' }
  );

  const [inputArea, setInputArea] = useState<string>('');
  const [inputStoreName, setInputStoreName] = useState<string>('');

  const [inputStartDate, setInputStartDate] = useState<string>('');
  const [inputEndDate, setInputEndDate] = useState<string>('');

  const searchResultRef = useRef<HTMLDivElement>(null);

  const handleSearchClicked = useCallback(() => {
    operators.search({
      area: inputArea,
      page: 1,
      dateEnd: inputEndDate,
      dateStart: inputStartDate,
      storeName: inputStoreName
    });
  }, [operators, inputArea, inputEndDate, inputStartDate, inputStoreName]);

  const handleDateSelected = useCallback((startDate: string, endDate: string) => {
    setInputStartDate(startDate);
    setInputEndDate(endDate);
  }, []);

  const handlePageClicked = useCallback((page: number) => {
    operators.paging(page);
    if (searchResultRef.current) {
      searchResultRef.current.scrollIntoView(true);
    }
  }, [operators]);

  return (
    <div className="osica-store-event-search-app">
      <SearchForm
        inputArea={inputArea} inputStoreName={inputStoreName} inputStartDate={inputStartDate} inputEndDate={inputEndDate}
        setInputArea={setInputArea} setDateRange={handleDateSelected} setInputStoreName={setInputStoreName}
        isLoading={isLoading} onSearchClicked={handleSearchClicked} />

      <div ref={searchResultRef} style={{ marginTop: 'var( --wp--style--block-gap )' }}>
        {
          errorMessage ? <p className='text-center text-danger'>{errorMessage}</p> : null
        }
        <ul className='wp-block-post-template wp-block-osica-osica-event-post-template' style={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 50ms' }}>
          {
            result.data.map(item => <StoreEventRow key={item.id} storeEvent={item} />)
          }
        </ul>
        {
          result.meta.total <= 0 ? <p className='text-muted text-center'>条件に一致するイベントが見つかりませんでした。</p> : null
        }
      </div>

      <nav className="wp-block-query-pagination" aria-label="ページネーション"
        style={{ justifyContent: 'center', display: 'flex', marginTop: 'var( --wp--style--block-gap )' }}>
        <Pager isLoading={isLoading} result={result} onPageClicked={handlePageClicked} />
      </nav>
    </div>
  );
}

export default App;


type useEventSearchFunctions = [
  SearchResult,
  string,
  boolean, {
    search: (query: SearchQuery) => void,
    paging: (page: number) => void,
  }
];

interface SearchQuery {
  area: string;
  storeName: string;
  dateStart: string;
  dateEnd: string;
  page: number;
}

function useEventSearch(id: number, initQuery: SearchQuery): useEventSearchFunctions {

  const initialLoaded = useRef(false);
  const [query, setQuery] = useState(initQuery);

  const [result, setResult] = useState<SearchResult>({
    data: [],
    links: null,
    meta: {
      current_page: 1,
      from: 1,
      last_page: 1,
      to: 1,
      total: 1
    }
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const search = useCallback((query: SearchQuery) => {
    setQuery(query);
  }, []);

  const paging = useCallback((page: number) => {
    setQuery(Object.assign({}, query, { page: page }));
  }, [query]);

  useEffect(() => {
    setIsLoading(true);

    let ignore = false;

    async function fetchEvents() {
      let prefix = '';
      if (window.location.hostname === 'localhost') {
        prefix = 'http://localhost/osica-member';
      } else {
        prefix = '/member';
      }

      const response = await fetch(prefix + '/api/storeeventapi/' + id + '/search?' + new URLSearchParams({
        name: query.storeName,
        name_match: 'contains',
        event_range_start: query.dateStart,
        event_range_end: query.dateEnd,
        area: query.area,
        per_page: '35',
        page: `${query.page}`,
        orderby: 'event_begin_at',
        direction: 'asc'
      }));

      if (response.status / 100 !== 2) {
        if (!ignore) {
          if (response.status === 404) {
            setErrorMessage('この大会一覧データは現在利用できません。時間を空けて再度お試しください。');
          } else {
            setErrorMessage('エラーが発生しました。時間を空けて再度お試しください。');
          }
          setIsLoading(false);
        }
      } else {
        const json = await response.json();
        if (!ignore) {
          setIsLoading(false);
          setResult(json);
          setErrorMessage('');
          initialLoaded.current = true;
        }
      }
    }

    setErrorMessage('');
    fetchEvents();
    return () => { ignore = true };
  }, [query, id]);

  return [
    result,
    errorMessage,
    isLoading,
    {
      search,
      paging
    }
  ];
}